/**
 * Hook: default and current screen plugin name from GuardRails rules for a course type.
 * @module Workspace/components/GuardRailsSelectorModal/components/ScreenTypeSelector/useScreenType
 */
import { useState, useMemo } from 'react';
import GuardRailsService from 'services/GuardRailsService';
import featureFlagService, { featureKeys } from 'services/FeatureFlagService';
import { GUARDRAILS_COURSE_TYPES } from 'services/GuardRailsService/GuardRailsCourseTypes';

/**
 * Pure resolver for the default screen plugin name (safe to call from event handlers).
 * @param {string} courseType - GuardRails course key (e.g. bronteViewer, ePub)
 * @param {boolean} isSlateJSGuardrailEnabled - BVEW-1232 Slate 2.0 guardrail flag
 * @returns {string}
 */
export function getDefaultScreenPluginName(currentCourse, isSlateJSGuardrailEnabled) {
  const plugins = GuardRailsService.getCourseScreenPlugins(currentCourse.courseType) || [];

  if(plugins.length>0) {
    const oldScreenPlugin = plugins[0].name;
    const newScreenPlugin = plugins[1].name;
    
    // existing course with supportedScreenVersion
    if(currentCourse?.supportedScreenVersion) {
      const {supportedScreenVersion} = currentCourse;
      return supportedScreenVersion === '2.*' ? newScreenPlugin : oldScreenPlugin;
    }

    // existing course without supportedScreenVersion
    if(currentCourse?.supportedScreenVersion === null) {
      return oldScreenPlugin;
    }

    if (currentCourse?.courseType === GUARDRAILS_COURSE_TYPES && isSlateJSGuardrailEnabled && plugins[1]) {
      return newScreenPlugin;
    }

    return oldScreenPlugin;
  }

}

/**
 * @param {string} currentCourseDetails - GuardRails course key (e.g. bronteViewer, ePub)
 * @returns {[string, (name: string) => void]} Selected screen plugin name and state setter.
 */
export default function useScreenType(currentCourseDetails) {
  const isSlateJSGuardrailEnabled = featureFlagService.is(featureKeys.BVEW_1232_Slate_JS_Guardrail);

  const defaultScreenType = useMemo(
    () => getDefaultScreenPluginName(currentCourseDetails, isSlateJSGuardrailEnabled),
    [currentCourseDetails, isSlateJSGuardrailEnabled],
  );

  const [selectedScreenType, setSelectedScreenType] = useState(defaultScreenType);

  return [selectedScreenType || defaultScreenType, setSelectedScreenType];
}